import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useCreateTicketMutation } from '../store/supportApi';
import {
  useListIssueCategoriesQuery,
  useGetContactInfoQuery,
} from '../store/siteContentApi';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import { ROUTES } from '../constants/routes';
import { Arrow } from '../components/home/Hero';
import {
  UI, EASE, wrap, wrapN, hXl, hM, hS, hXs, lead, eyebrow, sec,
  empty, panelCard, tile, cardDark, input, fieldLabel, req, kv, btn, btnWa,
  btnYellow, btnGhost, textbtn, spin, okmark, mono, num, ico as icoTile,
} from '../styles/tl5';


/* ════════════════════════ DATA ════════════════════════ */

// Issue categories + contact info are now admin-editable — they load from
// the backend (/api/issue-categories and /api/contact-info). Defaults below
// are only used as a no-flash fallback while the initial API request is in
// flight; the admin panel is the source of truth.
const CONTACT_DEFAULTS = {
  phone: '74400 75400',
  whatsapp: '+919014638633',
  email: 'support@mytrustlab.com',
  support_hours: '6:00 AM – 10:00 PM daily',
};

// Normalise the WhatsApp number into a wa.me-safe digit string so admins
// can type it in any format ("+91 90146 38633", "90146-38633", etc.).
const waHref = (whatsapp) => {
  const digits = String(whatsapp || '').replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}` : '#';
};

const ESCALATION_STEPS = [
  { step: 1, title: 'Front-line Support', desc: 'WhatsApp, call, or submit the form above. Our support executives handle most issues within 1–4 hours.', timing: 'Response: <4 hrs' },
  { step: 2, title: 'Senior Support Manager', desc: 'If unresolved after 4 hours, ask to escalate to a Senior Manager. Email escalation@mytrustlab.com with your ticket number.', timing: 'Response: <12 hrs' },
  { step: 3, title: 'Chief of Customer Experience', desc: 'For unresolved critical issues, write to ceo-office@mytrustlab.com. This inbox is reviewed by the leadership team personally.', timing: 'Response: <24 hrs' },
];

const RESPONSE_INDICATORS = [
  { label: 'WhatsApp: Immediate response' },
  { label: 'Phone: 9:30AM – 6PM daily' },
  { label: 'Email: within 4 hours' },
];

/* ═══════════════════════════════════════════════════════
   SUPPORT PAGE
   ═══════════════════════════════════════════════════════ */
// Must match OTHER_SUBCATEGORY in backend/src/controllers/supportController.js
// and the constant of the same name in the medical portal's RaiseQuery.js.
// All three have to agree or an "Other" sub-category arrives as a literal key.
const OTHER_SUBCATEGORY = '__other';

// Limits mirror the server's (MAX_ATTACHMENTS / MAX_ATTACHMENT_MB in
// backend/src/controllers/supportController.js). Checking here too is not
// belt-and-braces for its own sake: it tells the customer immediately rather
// than after a round trip that ends in a rejected complaint.
const MAX_FILES = 5;
const MAX_FILE_MB = 5;
const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf';

const prettyBytes = (n) => (n < 1024 * 1024
  ? `${Math.max(1, Math.round(n / 1024))} KB`
  : `${(n / (1024 * 1024)).toFixed(1)} MB`);

const fileKey = (f) => `${f.name}::${f.size}`;

const TicketAttachments = ({ files, onFiles }) => {
  const inputRef = useRef(null);
  const [error, setError] = useState('');
  const [previews, setPreviews] = useState({});

  // A blob URL per image, revoked when the list changes or we unmount.
  // Skipping the revoke strands every preview in memory for the life of the
  // tab, and swapping a photo twice is the normal case here.
  useEffect(() => {
    const made = {};
    for (const f of files) {
      if (f.type?.startsWith('image/')) made[fileKey(f)] = URL.createObjectURL(f);
    }
    setPreviews(made);
    return () => { for (const url of Object.values(made)) URL.revokeObjectURL(url); };
  }, [files]);

  const add = (incoming) => {
    setError('');
    const picked = Array.from(incoming || []);
    if (picked.length === 0) return;

    const tooBig = picked.find((f) => f.size > MAX_FILE_MB * 1024 * 1024);
    const seen = new Set(files.map(fileKey));
    const fresh = picked.filter(
      (f) => f.size <= MAX_FILE_MB * 1024 * 1024 && !seen.has(fileKey(f)),
    );
    const room = MAX_FILES - files.length;

    if (tooBig) setError(`${tooBig.name} is over ${MAX_FILE_MB} MB.`);
    else if (fresh.length > room) setError(`Up to ${MAX_FILES} files.`);

    if (room > 0) onFiles([...files, ...fresh.slice(0, room)]);
  };

  const atLimit = files.length >= MAX_FILES;

  return (
    <Box>
      <Box
        onClick={() => !atLimit && inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (!atLimit && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => { if (!atLimit) e.preventDefault(); }}
        onDrop={(e) => { if (!atLimit) { e.preventDefault(); add(e.dataTransfer.files); } }}
        sx={{
          px: 2, py: 2,
          border: '1.5px dashed rgba(0,0,0,.16)',
          borderRadius: '10px',
          textAlign: 'center',
          cursor: atLimit ? 'not-allowed' : 'pointer',
          opacity: atLimit ? 0.55 : 1,
          transition: 'border-color .15s, background .15s',
          '&:hover': !atLimit && { borderColor: 'var(--tl-teal)', bgcolor: 'rgba(0,0,0,.015)' },
        }}
      >
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: 'var(--tl-g9)' }}>
          {atLimit ? `Attachment limit reached (${MAX_FILES})` : 'Click to upload or drag & drop'}
        </Typography>
        <Typography sx={{ fontSize: 12, color: 'var(--tl-muted)', mt: 0.25 }}>
          JPG, PNG or PDF · up to {MAX_FILE_MB} MB each · max {MAX_FILES} files
        </Typography>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          onChange={(e) => { add(e.target.files); e.target.value = ''; }}
          style={{ display: 'none' }}
        />
      </Box>

      {error && (
        <Typography sx={{ fontSize: 12, color: '#B42318', mt: 0.75 }}>{error}</Typography>
      )}

      {files.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mt: '10px' }}>
          {files.map((file, i) => (
            <Box
              key={`${fileKey(file)}-${i}`}
              sx={{
                position: 'relative', width: 96,
                border: '1px solid rgba(0,0,0,.12)', borderRadius: '10px', overflow: 'hidden',
              }}
            >
              <Box sx={{ height: 62, bgcolor: 'rgba(0,0,0,.04)', display: 'grid', placeItems: 'center' }}>
                {previews[fileKey(file)] ? (
                  <Box
                    component="img"
                    src={previews[fileKey(file)]}
                    alt=""
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : (
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--tl-muted)' }}>PDF</Typography>
                )}
              </Box>
              <Box sx={{ px: 0.75, py: 0.5 }}>
                <Typography noWrap sx={{ fontSize: 10.5, color: 'var(--tl-g9)' }}>{file.name}</Typography>
                <Typography sx={{ fontSize: 9.5, color: 'var(--tl-muted)' }}>
                  {prettyBytes(file.size)}
                </Typography>
              </Box>
              <Box
                onClick={(e) => { e.stopPropagation(); onFiles(files.filter((_, idx) => idx !== i)); }}
                role="button"
                aria-label={`Remove ${file.name}`}
                sx={{
                  position: 'absolute', top: 4, right: 4,
                  width: 20, height: 20, borderRadius: '50%',
                  bgcolor: 'rgba(0,0,0,.6)', color: '#fff',
                  display: 'grid', placeItems: 'center',
                  fontSize: 12, lineHeight: 1, cursor: 'pointer',
                  '&:hover': { bgcolor: '#B42318' },
                }}
              >
                ×
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const SupIcon = ({ d, size = 20 }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    sx={{ width: size, height: size, minWidth: size, display: 'block' }}
  >
    {d}
  </Box>
);
const WA_ICON = <SupIcon d={<path d="M21 11.5a8.4 8.4 0 0 1-12.6 7.3L3 21l2.3-5.2A8.4 8.4 0 1 1 21 11.5Z" />} size={18} />;
const PHONE_ICON = <SupIcon d={<path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2Z" />} />;
const MAIL_ICON = <SupIcon d={<><rect x="2.5" y="5" width="19" height="14" rx="2.5" /><path d="m3 7 9 6 9-6" /></>} />;

const Support = () => {
  const { showToast } = useUI();
  const { user } = useAuth();

  // Admin-editable data.
  // The endpoint returns { categories, subcategories } — NOT a bare array.
  // Destructuring `data` straight into `categories` handed this component an
  // OBJECT, so `categories.map` threw and the whole page rendered blank.
  // `?? []` cannot help there: the object is not null, it is just the wrong
  // shape. See listActiveCategories in
  // backend/src/controllers/issueCategoryController.js.
  const { data: issueTypes } = useListIssueCategoriesQuery();
  const categories = useMemo(() => issueTypes?.categories ?? [], [issueTypes]);
  const subcategories = useMemo(() => issueTypes?.subcategories ?? [], [issueTypes]);
  const { data: contactFromApi } = useGetContactInfoQuery();
  const contact = contactFromApi || CONTACT_DEFAULTS;

  // The select drives the category; `setActiveCat` is still called so the
  // existing selectIssue handler keeps its single responsibility.
  const [, setActiveCat] = useState('');
  const [form, setForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    bookingId: '',
    category: '',
    subcategory: '',
    subcategoryOther: '',
    priority: 'normal',
    message: '',
    attachments: [],
  });
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState('');

  // Sub-categories belonging to the chosen category. Empty when that
  // category has none — an admin can add them to one category without being
  // forced to add them to all of them, and an empty dropdown reads as
  // broken, so the field only renders when there is something in it.
  const subOptions = useMemo(
    () => (form.category ? subcategories.filter((sc) => sc.parent_key === form.category) : []),
    [subcategories, form.category],
  );

  // Changing the category invalidates whatever sub-category was picked: it
  // belonged to the category that was just replaced.
  useEffect(() => {
    setForm((f) => (
      f.subcategory || f.subcategoryOther
        ? { ...f, subcategory: '', subcategoryOther: '' }
        : f
    ));
  }, [form.category]);

  // Once categories load, pick the first as the default selection so the
  // grid + dropdown aren't empty.
  useEffect(() => {
    if (!categories.length) return;
    setActiveCat((cur) => cur || categories[0].key);
    setForm((f) => (f.category ? f : { ...f, category: categories[0].key }));
  }, [categories]);

  // Prefill name + phone once the logged-in user becomes available (e.g. after
  // login on another tab, or if the context hydrates after first paint).
  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      name: f.name || user.name || '',
      phone: f.phone || user.phone || '',
    }));
  }, [user]);

  const update = useCallback(
    (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value })),
    [],
  );

  const selectIssue = useCallback((key) => {
    setActiveCat(key);
    setForm((f) => ({ ...f, category: key }));
  }, []);

  const [createTicket, { isLoading: submitting }] = useCreateTicketMutation();

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!form.name || !form.phone || !form.category || !form.message) {
        showToast('Please fill all required fields', 'warning');
        return;
      }
      // "Other" with nothing typed carries no meaning — the whole point of
      // the option is the words that follow it.
      if (form.subcategory === OTHER_SUBCATEGORY && !form.subcategoryOther.trim()) {
        showToast('Please tell us what the issue is', 'warning');
        return;
      }
      try {
        const res = await createTicket({
          name: form.name.trim(),
          phone: form.phone.trim(),
          booking_id: form.bookingId?.trim() || undefined,
          category: form.category,
          subcategory: form.subcategory || '',
          subcategory_other:
            form.subcategory === OTHER_SUBCATEGORY ? form.subcategoryOther.trim() : '',
          priority: form.priority,
          message: form.message.trim(),
          // Only present when files were picked; createTicket switches to
          // multipart on this and posts plain JSON otherwise.
          attachments: form.attachments,
        }).unwrap();
        const tid = res?.ticket?.ticket_id || '';
        setTicketId(tid);
        setSubmitted(true);
        showToast(`Complaint raised — reference ${tid}`);
      } catch (err) {
        showToast(err?.data?.message || 'Could not submit — please try again', 'error');
      }
    },
    [form, showToast, createTicket],
  );

  /* ─────────────────────── RENDER ─────────────────────── */
  const waLink = waHref(contact.whatsapp);
  const telLink = `tel:${String(contact.phone || '').replace(/[^\d+]/g, '')}`;
  const mailLink = `mailto:${contact.email}`;

  // ── submitted ──────────────────────────────────────────────────────
  if (submitted) {
    return (
      <Box component="section" sx={{ py: 'clamp(40px, 5vw, 72px)' }}>
        <Box sx={{ ...wrapN, ...empty }}>
          <Box sx={okmark}>
            <Box component="svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
              sx={{ width: 34, height: 34, display: 'block' }}>
              <path d="M20 6 9 17l-5-5" />
            </Box>
          </Box>
          <Typography component="h1" sx={{ ...hM, mt: '6px' }}>We&rsquo;ve got it</Typography>
          {ticketId && (
            <Typography sx={{ fontFamily: UI, fontSize: 16, color: 'var(--tl-muted)' }}>
              Your reference is <Box component="b" sx={{ ...mono, color: 'var(--tl-g9)' }}>{ticketId}</Box>
            </Typography>
          )}
          <Typography sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.6, color: 'var(--tl-muted)', maxWidth: 420 }}>
            Our team will call or WhatsApp you within 4 hours. You can follow the
            thread any time under My tickets.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px', mt: '6px' }}>
            <Box component={Link} to={ROUTES.MY_TICKETS} sx={{ ...btn, textDecoration: 'none' }}>
              View my tickets
              <Arrow size={16} />
            </Box>
            <Box component="a" href={waLink} target="_blank" rel="noopener noreferrer" sx={{ ...btnGhost, textDecoration: 'none' }}>
              Chat on WhatsApp
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* ═══ HERO ═══ */}
      {/* Matches Help's hero — Help is where people arrive from, and the
          two pages reading as one flow matters more than variety here. */}
      <Box component="section" sx={{ pt: 2, px: 2 }}>
        <Box
          sx={{
            width: '100%',
            maxWidth: 1320,
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2.25,
            p: 'clamp(28px, 5vw, 56px)',
            borderRadius: '36px',
            bgcolor: 'var(--tl-teal)',
            color: '#FFFFFF',
          }}
        >
          <Box component="span" sx={eyebrow}>Customer support</Box>
          <Typography component="h1" sx={{ ...hXl, color: '#FFFFFF' }}>
            We&rsquo;re here for you.{' '}
            <Box component="span" sx={{ color: 'var(--tl-teal-l)' }}>Always.</Box>
          </Typography>
          <Typography sx={{ m: 0, fontFamily: UI, fontSize: 18, lineHeight: 1.55, color: '#E6FAF5', maxWidth: 620 }}>
            The fastest way to reach us is WhatsApp — a real person replies in minutes.
          </Typography>
          <Box
            component="a"
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ ...btnWa, alignSelf: 'flex-start', minHeight: 54, textDecoration: 'none' }}
          >
            {WA_ICON}
            Chat on WhatsApp
          </Box>
        </Box>
      </Box>

      {/* ═══ FORM + ASIDE ═══ */}
      <Box component="section" sx={sec}>
        <Box sx={wrap}>
          <Box sx={{ mb: '22px' }}>
            <Typography component="h2" sx={hM}>What can we help with?</Typography>
            <Typography sx={{ ...lead, mt: '8px' }}>
              Select your query, issue or complaint type and tell us what happened.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.5fr) minmax(0, 1fr)' },
              gap: '20px',
              alignItems: 'start',
            }}
          >
            {/* ── the form ── */}
            <Box component="form" onSubmit={handleSubmit} sx={panelCard}>
              <Typography component="h3" sx={hS}>Raise a complaint or request</Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '18px', mt: '20px' }}>
                <Box>
                  <Box component="label" htmlFor="sp-name" sx={fieldLabel}>
                    Your name<Box component="span" sx={req}> *</Box>
                  </Box>
                  <Box component="input" id="sp-name" value={form.name} onChange={update('name')} autoComplete="name" sx={input} />
                </Box>

                <Box>
                  <Box component="label" htmlFor="sp-phone" sx={fieldLabel}>
                    Mobile number<Box component="span" sx={req}> *</Box>
                  </Box>
                  <Box
                    component="input"
                    id="sp-phone"
                    value={form.phone}
                    onChange={update('phone')}
                    placeholder="+91 XXXXXXXXXX"
                    inputMode="tel"
                    autoComplete="tel"
                    sx={{ ...input, ...num }}
                  />
                </Box>

                <Box>
                  <Box component="label" htmlFor="sp-booking" sx={fieldLabel}>
                    Booking ID <Box component="span" sx={{ color: 'var(--tl-muted)', fontWeight: 500 }}>(optional)</Box>
                  </Box>
                  <Box
                    component="input"
                    id="sp-booking"
                    value={form.bookingId}
                    onChange={update('bookingId')}
                    placeholder="TL-2026-XXXXX"
                    sx={{ ...input, ...mono, fontSize: 15 }}
                  />
                </Box>

                <Box>
                  <Box component="label" htmlFor="sp-cat" sx={fieldLabel}>
                    Issue category<Box component="span" sx={req}> *</Box>
                  </Box>
                  <Box
                    component="select"
                    id="sp-cat"
                    value={form.category}
                    onChange={(e) => selectIssue(e.target.value)}
                    sx={{ ...input, cursor: 'pointer' }}
                  >
                    <option value="">— Select issue type —</option>
                    {categories.map((c) => (
                      <option key={c.key} value={c.key}>{c.title || c.key}</option>
                    ))}
                  </Box>
                </Box>

                {/* Sub-category only when the chosen category has any. */}
                {subOptions.length > 0 && (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Box component="label" htmlFor="sp-sub" sx={fieldLabel}>What specifically?</Box>
                    <Box
                      component="select"
                      id="sp-sub"
                      value={form.subcategory}
                      onChange={update('subcategory')}
                      sx={{ ...input, cursor: 'pointer' }}
                    >
                      <option value="">— Select —</option>
                      {subOptions.map((sc) => (
                        <option key={sc.key} value={sc.key}>{sc.title || sc.key}</option>
                      ))}
                      <option value={OTHER_SUBCATEGORY}>Other</option>
                    </Box>
                  </Box>
                )}

                {form.subcategory === OTHER_SUBCATEGORY && (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Box component="label" htmlFor="sp-sub-other" sx={fieldLabel}>
                      Tell us what the issue is<Box component="span" sx={req}> *</Box>
                    </Box>
                    <Box
                      component="input"
                      id="sp-sub-other"
                      value={form.subcategoryOther}
                      onChange={update('subcategoryOther')}
                      sx={input}
                    />
                  </Box>
                )}

                {/* Priority — two cards rather than a radio list, as the
                    reference has, so the consequence of Urgent is visible. */}
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Box component="span" sx={fieldLabel}>
                    Priority<Box component="span" sx={req}> *</Box>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '12px' }}>
                    {[['normal', 'Normal', ''], ['urgent', 'Urgent', 'Handled within 1 hour']].map(([v, label, note]) => {
                      const on = form.priority === v;
                      return (
                        <Box
                          key={v}
                          component="button"
                          type="button"
                          role="radio"
                          aria-checked={on}
                          onClick={() => setForm((f) => ({ ...f, priority: v }))}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: '12px', textAlign: 'left',
                            minHeight: 62, px: '18px', borderRadius: '16px',
                            border: '1.5px solid', borderColor: on ? 'var(--tl-teal)' : 'var(--tl-line-2)',
                            background: on ? 'var(--tl-teal-50)' : '#fff',
                            cursor: 'pointer', transition: `all .25s ${EASE}`,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'grid', placeItems: 'center', width: 20, height: 20, flex: 'none',
                              borderRadius: '50%', border: '2px solid',
                              borderColor: on ? 'var(--tl-teal-d)' : 'var(--tl-line-2)',
                            }}
                          >
                            {on && <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: 'var(--tl-teal-d)' }} />}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ fontFamily: UI, fontSize: 15.5, fontWeight: 700, color: 'var(--tl-g9)' }}>{label}</Box>
                            {note && <Box sx={{ fontFamily: UI, fontSize: 13, color: 'var(--tl-muted)' }}>{note}</Box>}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Box component="label" htmlFor="sp-msg" sx={fieldLabel}>
                    Describe the issue<Box component="span" sx={req}> *</Box>
                  </Box>
                  <Box
                    component="textarea"
                    id="sp-msg"
                    rows={5}
                    value={form.message}
                    onChange={update('message')}
                    placeholder="What happened, and when?"
                    sx={{ ...input, height: 'auto', minHeight: 130, py: '14px', resize: 'vertical', lineHeight: 1.55 }}
                  />
                </Box>

                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Box component="span" sx={fieldLabel}>
                    Attach a photo or PDF <Box component="span" sx={{ color: 'var(--tl-muted)', fontWeight: 500 }}>(optional)</Box>
                  </Box>
                  <TicketAttachments
                    files={form.attachments}
                    onFiles={(files) => setForm((f) => ({ ...f, attachments: files }))}
                  />
                </Box>
              </Box>
              

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '14px', mt: '24px' }}>
                <Typography sx={{ fontFamily: UI, fontSize: 14, color: 'var(--tl-muted)' }}>
                  Our team will call or WhatsApp you within 4 hours.
                </Typography>
                <Box component="button" type="submit" disabled={submitting} sx={{ ...btnYellow, minHeight: 54, '&:disabled': { opacity: 0.55, pointerEvents: 'none' } }}>
                  {submitting && <Box component="span" sx={{ ...spin, width: 16, height: 16, borderWidth: 2 }} />}
                  {submitting ? 'Submitting…' : 'Submit'}
                </Box>
              </Box>
            </Box>

            {/* ── aside ── */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }}>
              <Typography component="h3" sx={hS}>Our service commitments</Typography>

              {[
                { icon: WA_ICON, title: 'WhatsApp support', sub: 'Online now', action: 'Chat', href: waLink, external: true },
                { icon: PHONE_ICON, title: 'Call us', sub: contact.support_hours, action: 'Call now', href: telLink },
                { icon: MAIL_ICON, title: 'Email support', sub: 'Reply within 4 hours', action: 'Send email', href: mailLink },
              ].map((ch) => (
                <Box key={ch.title} sx={{ ...panelCard, p: '18px 20px', display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <Box sx={{ ...icoTile, width: 44, height: 44, marginBottom: 0, borderRadius: '14px' }}>{ch.icon}</Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography component="h4" sx={hXs}>{ch.title}</Typography>
                    <Box sx={{ fontFamily: UI, fontSize: 13.5, color: 'var(--tl-muted)', mt: '2px' }}>{ch.sub}</Box>
                  </Box>
                  <Box
                    component="a"
                    href={ch.href}
                    {...(ch.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    sx={{ ...textbtn, flex: 'none', textDecoration: 'none' }}
                  >
                    {ch.action}
                  </Box>
                </Box>
              ))}

              <Box component="dl" sx={{ ...tile, p: '6px 20px', m: 0 }}>
                {RESPONSE_INDICATORS.map((r, i) => (
                  <Box key={r.label} sx={{ ...kv, gridTemplateColumns: '1fr auto', borderBottom: i === RESPONSE_INDICATORS.length - 1 ? 0 : '1px solid var(--tl-line)' }}>
                    <Box component="dt">{r.label.split(':')[0]}</Box>
                    <Box component="dd" sx={{ textAlign: 'right' }}>{r.label.split(':').slice(1).join(':').trim()}</Box>
                  </Box>
                ))}
              </Box>

              {/* Escalation ladder */}
              <Box sx={cardDark}>
                <Typography component="h3" sx={{ ...hS, color: '#fff' }}>Not happy with the response?</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px', mt: '18px' }}>
                  {ESCALATION_STEPS.map((st) => (
                    <Box key={st.step} sx={{ display: 'flex', gap: '14px' }}>
                      <Box
                        sx={{
                          display: 'grid', placeItems: 'center', width: 28, height: 28, flex: 'none',
                          borderRadius: '50%', background: 'rgba(95,224,198,.14)',
                          border: '1.5px solid rgba(95,224,198,.5)', color: 'var(--tl-teal-l)',
                          fontFamily: UI, fontSize: 12.5, fontWeight: 800,
                        }}
                      >
                        {st.step}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ fontFamily: UI, fontSize: 15.5, fontWeight: 700, color: '#fff' }}>{st.title}</Box>
                        <Box sx={{ fontFamily: UI, fontSize: 13.5, lineHeight: 1.55, color: '#B6CFC8', mt: '3px' }}>
                          {st.timing.replace('Response: ', '')} · {st.desc}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Support;
